/**
 * User Service - Handles user roles and profile operations
 * Uses Supabase tables: user_roles_catalog, user_roles_translations, profiles
 */

import { supabase } from '../lib/supabase';

/**
 * Get professional roles with translations
 * @param {string} lang - Language code ('it', 'en', etc.)
 * @returns {Promise<Array<{code: string, label: string, description: string, userType: string, icon: string}>>}
 */
export const getProfessionalRoles = async (lang = 'it') => {
  if (!supabase) {
    console.warn('Supabase not configured, returning empty roles');
    return [];
  }

  try {
    console.log('🔍 Fetching roles from user_roles_catalog...');
    
    // Step 1: Get roles from catalog (simple query, no joins)
    const { data: catalogData, error: catalogError } = await supabase
      .from('user_roles_catalog')
      .select('code, category');

    if (catalogError) {
      console.error('❌ Error fetching catalog:', catalogError);
      throw catalogError;
    }

    if (!catalogData || catalogData.length === 0) {
      console.warn('⚠️ No roles found in catalog');
      return [];
    }

    console.log('✅ Catalog loaded:', catalogData.length, 'roles');
    console.log('📋 Codes:', catalogData.map(r => r.code).join(', '));

    // Step 2: Get translations separately
    const roleCodes = catalogData.map(r => r.code);
    const { data: translationsData, error: translationsError } = await supabase
      .from('user_roles_translations')
      .select('role_code, label, description')
      .eq('lang', lang)
      .in('role_code', roleCodes);

    if (translationsError) {
      console.warn('⚠️ Translations error (using code as label):', translationsError.message);
    } else {
      console.log('✅ Translations loaded:', translationsData?.length || 0);
    }

    // Build translation map
    const translationMap = {};
    (translationsData || []).forEach(t => {
      translationMap[t.role_code] = { label: t.label, description: t.description };
    });

    // Step 3: Merge
    const roles = catalogData.map(role => ({
      code: role.code,
      label: translationMap[role.code]?.label || role.code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      description: translationMap[role.code]?.description || '',
      category: role.category
    }));

    console.log('✅ Final roles:', JSON.stringify(roles, null, 2));
    return roles;

  } catch (err) {
    console.error('❌ getProfessionalRoles error:', err.message || err);
    return [];
  }
};

/**
 * Get user type based on professional role
 * @param {string} roleProfessional - The professional role code
 * @returns {string} - 'pro', 'advisor', or 'private'
 */
export const getUserTypeFromRole = (roleProfessional) => {
  const advisorRoles = ['advisor'];
  const privateRoles = ['private_client'];
  
  if (advisorRoles.includes(roleProfessional)) return 'advisor';
  if (privateRoles.includes(roleProfessional)) return 'private';
  return 'pro';
};

/**
 * Get user profile from profiles table
 * @param {string} userId - The auth user ID
 * @returns {Promise<Object|null>}
 */
export const getUserProfile = async (userId) => {
  if (!supabase || !userId) return null;

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No profile found - will be created on first update
        return null;
      }
      throw error;
    }

    return data;
  } catch (err) {
    console.error('getUserProfile error:', err);
    return null;
  }
};

/**
 * Update or create user profile
 * @param {string} userId - The auth user ID
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const updateUserProfile = async (userId, profileData) => {
  if (!supabase || !userId) {
    return { success: false, error: 'Supabase not configured or missing user ID' };
  }

  try {
    // Calculate user_type if role_professional is provided
    if (profileData.role_professional && !profileData.user_type) {
      profileData.user_type = getUserTypeFromRole(profileData.role_professional);
    }

    // Add updated_at
    profileData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('profiles')
      .upsert({
        id: userId,
        ...profileData
      }, {
        onConflict: 'id'
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (err) {
    console.error('updateUserProfile error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Complete user onboarding
 * @param {string} userId - The auth user ID
 * @param {Object} onboardingData - Onboarding form data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const completeOnboarding = async (userId, onboardingData) => {
  const { 
    first_name, 
    last_name, 
    company, 
    role_professional, 
    city, 
    state,
    phone 
  } = onboardingData;

  const user_type = getUserTypeFromRole(role_professional);

  return updateUserProfile(userId, {
    first_name,
    last_name,
    company,
    role_professional,
    user_type,
    city,
    state,
    phone,
    onboarding_completed: true,
    role_system: 'user'  // Default role
  });
};

export default {
  getProfessionalRoles,
  getUserTypeFromRole,
  getUserProfile,
  updateUserProfile,
  completeOnboarding
};
